import React, { useEffect, useState } from 'react';
import {
  Card,
  Descriptions,
  Typography,
  Space,
  Button,
  Tag,
  Spin,
  message,
  Row,
  Col,
  Modal,
  Form,
  Input,
  InputNumber,
  Alert,
} from 'antd';
import {
  EditOutlined,
  GlobalOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  KeyOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { BrandService } from '../services/brandService';
import { Brand } from '../types';
import { getActiveBrandId } from '../config/api';

const { Title, Text, Paragraph } = Typography;

const BrandProfile: React.FC = () => {
  const { user } = useAuth();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
  const [generatedApiSecret, setGeneratedApiSecret] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [apiKeyForm] = Form.useForm();

  // Get active brand ID based on environment (staging uses stageBrandId, prod uses brandId)
  const activeBrandId = getActiveBrandId(user);

  useEffect(() => {
    loadBrandProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrandId]);

  const loadBrandProfile = async () => {
    try {
      setLoading(true);
      if (activeBrandId) {
        const brandData = await BrandService.getBrandById(activeBrandId);
        setBrand(brandData);
        form.setFieldsValue(brandData);
      }
    } catch (error) {
      message.error('Marka bilgileri yüklenemedi');
      console.error('Failed to load brand profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBrand = async (values: any) => {
    try {
      if (!activeBrandId) return;
      await BrandService.updateBrand(activeBrandId, values);
      message.success('Marka bilgileri güncellendi');
      setEditModalVisible(false);
      loadBrandProfile();
    } catch (error) {
      message.error('Güncelleme başarısız');
    }
  };

  const handleGenerateApiKey = async (values: any) => {
    try {
      if (!activeBrandId) return;
      
      const { allowedDomains, monthlyLimit } = values;
      const domainsArray = allowedDomains 
        ? allowedDomains.split(',').map((d: string) => d.trim()).filter(Boolean)
        : [];

      const result = await BrandService.generateApiKey(
        activeBrandId,
        domainsArray,
        monthlyLimit || 10000
      );

      setGeneratedApiKey(result.apiKey);
      setGeneratedApiSecret(result.apiSecret || null);
      message.success('API Key başarıyla üretildi');
      loadBrandProfile();
    } catch (error: any) {
      message.error(error.message || 'API Key üretilemedi');
    }
  };

  const handleApiKeyModalClose = () => {
    setApiKeyModalVisible(false);
    setGeneratedApiKey(null);
    setGeneratedApiSecret(null);
    apiKeyForm.resetFields();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!brand) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Text type="secondary">Marka bilgisi bulunamadı</Text>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Marka Profilim</Title>
          <Text type="secondary">Markanızın detaylı bilgileri</Text>
        </div>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => setEditModalVisible(true)}
        >
          Düzenle
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Marka Bilgileri">
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Marka Adı">
                <Text strong>{brand.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Marka ID">
                <Text copyable code>{brand.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Website">
                {brand.website ? (
                  <a href={brand.website} target="_blank" rel="noopener noreferrer">
                    <GlobalOutlined /> {brand.website}
                  </a>
                ) : (
                  <Text type="secondary">Belirtilmemiş</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="XML Feed URL">
                {brand.xmlFeedUrl ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text copyable code style={{ wordBreak: 'break-all' }}>
                      {brand.xmlFeedUrl}
                    </Text>
                  </Space>
                ) : (
                  <Text type="secondary">Yapılandırılmamış</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="API Key">
                <Space direction="vertical" style={{ width: '100%' }}>
                  {brand.apiKey ? (
                    <Space>
                      <Text code copyable>{brand.apiKey}</Text>
                      <Tag color="green">Aktif</Tag>
                    </Space>
                  ) : (
                    <Tag color="red">Yapılandırılmamış</Tag>
                  )}
                  <Button
                    type="dashed"
                    icon={<KeyOutlined />}
                    size="small"
                    onClick={() => setApiKeyModalVisible(true)}
                  >
                    {brand.apiKey ? 'Yeni API Key Üret' : 'API Key Üret'}
                  </Button>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={brand.isActive ? 'green' : 'red'} icon={brand.isActive ? <CheckCircleOutlined /> : undefined}>
                  {brand.isActive ? 'Aktif' : 'Pasif'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Oluşturulma Tarihi">
                {new Date(brand.createdAt).toLocaleDateString('tr-TR')}
              </Descriptions.Item>
              <Descriptions.Item label="Son Güncelleme">
                {brand.updatedAt ? new Date(brand.updatedAt).toLocaleDateString('tr-TR') : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Marka Bilgilerini Düzenle"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateBrand}
        >
          <Form.Item
            name="name"
            label="Marka Adı"
            rules={[{ required: true, message: 'Marka adı zorunludur' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="website"
            label="Website"
            rules={[{ type: 'url', message: 'Geçerli bir URL girin' }]}
          >
            <Input prefix={<GlobalOutlined />} placeholder="https://example.com" />
          </Form.Item>

          <Form.Item
            name="xmlFeedUrl"
            label="XML Feed URL"
            rules={[{ type: 'url', message: 'Geçerli bir URL girin' }]}
          >
            <Input prefix={<ApiOutlined />} placeholder="https://example.com/products.xml" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Kaydet
              </Button>
              <Button onClick={() => setEditModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="API Key Üret"
        open={apiKeyModalVisible}
        onCancel={handleApiKeyModalClose}
        footer={null}
        width={600}
      >
        {!generatedApiKey ? (
          <Form
            form={apiKeyForm}
            layout="vertical"
            onFinish={handleGenerateApiKey}
            initialValues={{
              monthlyLimit: 10000,
            }}
          >
            <Alert
              message="Uyarı"
              description="Yeni bir API key üretildiğinde eski key geçersiz olacaktır. Lütfen yeni key'i güvenli bir yerde saklayın."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              name="allowedDomains"
              label="İzin Verilen Domainler"
              extra="Virgülle ayrılmış domain listesi (örn: example.com, test.com). Boş bırakırsanız tüm domainler izinli olur."
            >
              <Input.TextArea
                rows={3}
                placeholder="example.com, test.com"
              />
            </Form.Item>

            <Form.Item
              name="monthlyLimit"
              label="Aylık İstek Limiti"
              rules={[{ required: true, message: 'Aylık limit zorunludur' }]}
            >
              <InputNumber
                min={1}
                max={1000000}
                style={{ width: '100%' }}
                placeholder="10000"
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<KeyOutlined />}>
                  Üret
                </Button>
                <Button onClick={handleApiKeyModalClose}>
                  İptal
                </Button>
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message="API Key Başarıyla Üretildi"
              description="Lütfen bu bilgileri güvenli bir yerde saklayın. API Secret sadece şimdi görüntülenebilir."
              type="success"
              showIcon
            />

            <Card title="API Key" size="small">
              <Paragraph copyable={{ text: generatedApiKey }}>
                <Text code>{generatedApiKey}</Text>
              </Paragraph>
            </Card>

            {generatedApiSecret && (
              <Card title="API Secret" size="small">
                <Alert
                  message="Bu bilgi sadece bir kez gösterilecektir!"
                  type="error"
                  showIcon
                  style={{ marginBottom: 8 }}
                />
                <Paragraph copyable={{ text: generatedApiSecret }}>
                  <Text code>{generatedApiSecret}</Text>
                </Paragraph>
              </Card>
            )}

            <Button 
              type="primary" 
              onClick={handleApiKeyModalClose}
              block
              icon={<CopyOutlined />}
            >
              Kopyaladım, Kapat
            </Button>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default BrandProfile;

